import type { AlertEvent, AlertRule, ApplicationWithStats } from '@owl/shared';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { api } from '../../../lib/api';
import { timeAgo } from '../../../lib/time';
import { useLoader } from '../../../lib/useLoader';
import { Icon } from '../../icons';
import { SeverityChip } from '../../shared';
import type { AlertsTab, MobileShellCtx } from '../MobileShellCtx';

const SEV_COLOR: Record<AlertRule['severity'], string> = {
  critical: '#ff8b8b',
  high: '#ff9a6c',
  medium: '#f5c373',
  low: '#B4B4BA',
};

export function AlertsPage({ ctx, tab }: { ctx: MobileShellCtx; tab: AlertsTab }) {
  const setTab = (next: AlertsTab) => ctx.navigate({ name: 'alerts', tab: next });
  return (
    <div className="m-page">
      <div className="m-tabs">
        <button
          type="button"
          className={`m-tab ${tab === 'events' ? 'active' : ''}`}
          onClick={() => setTab('events')}
        >
          Eventos
        </button>
        <button
          type="button"
          className={`m-tab ${tab === 'rules' ? 'active' : ''}`}
          onClick={() => setTab('rules')}
        >
          Reglas
        </button>
      </div>
      {tab === 'events' ? <EventsTab ctx={ctx} /> : <RulesTab ctx={ctx} />}
    </div>
  );
}

function EventsTab({ ctx }: { ctx: MobileShellCtx }) {
  const [events, setEvents] = useState<AlertEvent[]>([]);
  const [apps, setApps] = useState<ApplicationWithStats[]>([]);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(
    async (isCancelled: () => boolean) => {
      setLoading(true);
      setErr(null);
      try {
        const [e, a, r] = await Promise.all([
          api.listAlertEvents({ environment: ctx.environment, limit: 50 }),
          api.listApplications(ctx.environment),
          api.listAlertRules(),
        ]);
        if (isCancelled()) return;
        setEvents(e.items);
        setCursor(e.nextCursor);
        setApps(a.items);
        setRules(r.items);
      } catch (ex) {
        if (!isCancelled()) setErr((ex as Error).message);
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    [ctx.environment],
  );

  useLoader(load, ctx.refreshTick);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await api.listAlertEvents({
        environment: ctx.environment,
        limit: 50,
        cursor,
      });
      setEvents((prev) => [...prev, ...r.items]);
      setCursor(r.nextCursor);
    } finally {
      setLoadingMore(false);
    }
  };

  const ruleById = useMemo(() => new Map(rules.map((r) => [r.id, r])), [rules]);
  const appById = useMemo(() => new Map(apps.map((a) => [a.id, a])), [apps]);

  if (loading) return <div className="m-loading">Cargando…</div>;
  if (err) return <div className="m-error-banner">{err}</div>;
  if (events.length === 0) return <div className="m-empty">Sin eventos</div>;

  return (
    <>
      <section className="m-card">
        {events.map((ev) => {
          const rule = ruleById.get(ev.ruleId);
          const app = appById.get(ev.applicationId);
          const sevColor = rule ? SEV_COLOR[rule.severity] : SEV_COLOR.low;
          const ch = ev.channelsResult[0];
          const chColor =
            ch?.status === 'sent'
              ? '#92d3a3'
              : ch?.status === 'failed'
                ? '#ff9a9a'
                : 'var(--m-fg-3)';
          const chBorder =
            ch?.status === 'sent'
              ? 'rgba(92,179,119,0.3)'
              : ch?.status === 'failed'
                ? 'rgba(239,68,68,0.3)'
                : 'var(--m-border-2)';
          return (
            <button
              key={ev.id}
              type="button"
              className="alert-event"
              onClick={() => ctx.openSheet({ kind: 'alertEvent', eventId: ev.id })}
            >
              <div style={{ background: sevColor, opacity: 0.7 }} />
              <div className="body">
                <div className="top">
                  {rule && <SeverityChip severity={rule.severity} />}
                  {ch && (
                    <span
                      className="chip"
                      style={{
                        fontSize: 10,
                        padding: '1px 6px',
                        color: chColor,
                        borderColor: chBorder,
                      }}
                    >
                      <Icon.Mail size={9} /> {ch.status}
                    </span>
                  )}
                  <span
                    className="mono"
                    style={{ fontSize: 11.5, color: 'var(--m-fg-4)', marginLeft: 'auto' }}
                  >
                    {timeAgo(ev.triggeredAt)}
                  </span>
                </div>
                <div className="name">{rule?.name ?? '—'}</div>
                <div className="sub">{app?.name ?? '—'}</div>
              </div>
              <div className="rt">
                <Icon.ChevronRight size={14} />
              </div>
            </button>
          );
        })}
      </section>
      {cursor && (
        <button
          type="button"
          className="m-list-row"
          style={{
            justifyContent: 'center',
            background: 'var(--m-bg-2)',
            borderRadius: 'var(--m-radius-card)',
            border: '1px solid var(--m-border-1)',
            marginTop: 12,
          }}
          onClick={loadMore}
          disabled={loadingMore}
        >
          <span style={{ color: 'var(--m-fg-2)', fontSize: 13 }}>
            {loadingMore ? 'Cargando…' : 'Cargar más'}
          </span>
        </button>
      )}
    </>
  );
}

function RulesTab({ ctx }: { ctx: MobileShellCtx }) {
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load2 = useCallback(async (isCancelled: () => boolean) => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.listAlertRules();
      if (!isCancelled()) setRules(r.items);
    } catch (e) {
      if (!isCancelled()) setErr((e as Error).message);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, []);

  useLoader(load2, ctx.refreshTick);

  if (loading) return <div className="m-loading">Cargando…</div>;
  if (err) return <div className="m-error-banner">{err}</div>;
  if (rules.length === 0) return <div className="m-empty">Sin reglas</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rules.map((r) => {
        const appCount =
          r.scopeApplications === 'all' ? 'Todas las apps' : `${r.scopeApplications.length} apps`;
        return (
          <button
            key={r.id}
            type="button"
            className="rule-card"
            onClick={() => ctx.openSheet({ kind: 'alertRule', ruleId: r.id })}
          >
            <div className="rule-card-h">
              <div>
                <div className="rule-card-name">{r.name}</div>
                <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <SeverityChip severity={r.severity} />
                  <span
                    className="chip"
                    style={{
                      fontSize: 10,
                      color: r.status === 'active' ? '#92d3a3' : 'var(--m-fg-3)',
                      padding: '1px 7px',
                    }}
                  >
                    <span
                      className={`dot ${r.status === 'active' ? 'active' : 'paused'}`}
                      style={{ width: 6, height: 6 }}
                    />
                    {r.status}
                  </span>
                </div>
              </div>
            </div>
            <div className="rule-cond">{renderCondition(r)}</div>
            <div className="rule-card-meta">
              <span>{appCount}</span>
              <span className="sep">·</span>
              <span>
                <Icon.Mail size={11} style={{ verticalAlign: '-2px', marginRight: 3 }} />
                {r.channels.email.length}
              </span>
              <span className="sep">·</span>
              <span>{r.triggerCount7d} disparos / 7d</span>
              <span className="sep">·</span>
              <span>{r.lastTriggeredAt ? timeAgo(r.lastTriggeredAt) : 'nunca'}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function renderCondition(r: AlertRule): ReactNode {
  const c = r.condition;
  switch (c.type) {
    case 'any_match':
      return <>Cualquier coincidencia en el ámbito de la regla.</>;
    case 'threshold':
      return (
        <>
          Más de <span className="accent">{c.count}</span> en{' '}
          <span className="accent">{c.windowMinutes}m</span>.
        </>
      );
    case 'content_match':
      return (
        <>
          Match{' '}
          <span className="accent">
            {c.isRegex ? '/' : '"'}
            {c.pattern}
            {c.isRegex ? '/' : '"'}
          </span>{' '}
          en el mensaje.
        </>
      );
    case 'metadata_match':
      return (
        <>
          metadata.<span className="accent">{c.key}</span> {c.operator}{' '}
          <span className="accent">{c.value}</span>
        </>
      );
  }
}
